import {
	Alert,
	Box,
	Button,
	ColumnLayout,
	Container,
	ExpandableSection,
	Form,
	FormField,
	Header,
	Input,
	RadioGroup,
	SpaceBetween
} from '@cloudscape-design/components'
import { useOktaAuth } from '@okta/okta-react'
import { FC, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import FormDayTime from '../../../components/ControlledFormInputs/DayTime'
import FormInput from '../../../components/ControlledFormInputs/Input'
import FormMultiselect from '../../../components/ControlledFormInputs/Multiselect'
// import FormRequiredTags from '../../../components/ControlledFormInputs/RequiredTags'

import type { ProjectRequest } from '../../../types/projects'
import { useNewProjectFormStore } from '../../../stores/projects/newProjectFormStore'
import useCreateNewProject from '../../../hooks/mutate/useCreateNewProject'
import FormTokenGroup from '../../../components/ControlledFormInputs/InputTokenGroup'
import FormTagEditor from '../../../components/ControlledFormInputs/TagEditor'
import useTagsByAccountId from '../../../hooks/query/useTagsByAccountId'
import ReadGenericTags from '../../../components/ControlledFormInputs/GenericTagsRead'
import GenericTagsNew from '../../../components/ControlledFormInputs/GenericTagsNew'
import { useGenericTagsManagementFormStore } from '../../../stores/tags/genericTagsManagementFormStore'
import { TagsRequest } from '../../../types/tags'
import {
	buildAwsFundingTagRequest,
	fundingDetailsFromParent,
	getAwsFundingApprovals
} from '../../../helper/awsFundingApproval'
import useSingleTags from '../../../hooks/query/useSingleTags'
import ApproverOverview from '../../../components/ControlledFormInputs/ApproverOverview'
import useCreateNewTags from '../../../hooks/mutate/useCreateNewTags'
import { v4 as uuidv4 } from 'uuid'
import FormDatePicker from '../../../components/ControlledFormInputs/FormDatePicker'
import FormSelect from '../../../components/ControlledFormInputs/Select'
import PORTFOLIO_LIST from '../../../portfolio'
import useAccounts from '../../../hooks/query/useAccounts'
import useClusters from '../../../hooks/query/useClusters'
import type { Account } from '../../../types/account'
import type { Cluster } from '../../../types/cluster'
import {
	buildProjectDistributionListOptions,
	normalizeAccountWorkloadType,
	resolveDistributionListValue
} from '../../../helper/projectDistributionListOptions'

const weekDaySelectOptions = [
	{ label: 'Monday', value: 'monday' },
	{ label: 'Tuesday', value: 'tuesday' },
	{ label: 'Wednesday', value: 'wednesday' },
	{ label: 'Thursday', value: 'thursday' },
	{ label: 'Friday', value: 'friday' },
	{ label: 'Saturday', value: 'saturday' },
	{ label: 'Sunday', value: 'sunday' }
]


const ENVIRONMENT_LIST = [
	{ label: 'dv', value: 'dv' },
	{ label: 'st', value: 'st' },
	{ label: 'pd', value: 'pd' }
]

const NewProjectForm: FC = () => {
	const { authState } = useOktaAuth()
	const thisUserEmail = authState?.idToken?.claims.email as string

	const navigate = useNavigate()

	const {
		formValues,
		actions: { setFormValues, setFormValidity }
	} = useNewProjectFormStore()

	const { mutate: createNewProject, isLoading } = useCreateNewProject()
	const { mutate: getTagsByAccountId, isLoading: isLoadingTags } =
		useTagsByAccountId()
	const { data: accounts, isLoading: isLoadingAccounts } = useAccounts()
	const { data: clustersList, isLoading: isLoadingClusters } = useClusters()
	const [getTagsId, setTagsId] = useState('')
	const { data: accountTags } = useSingleTags(getTagsId, { enabled: !!getTagsId })
	const [tagsLoadStatus, setTagsLoadStatus] = useState<
		'idle' | 'loading' | 'loaded' | 'error'
	>('idle')
	const [tagsViewToggler, setTagsViewToggler] = useState(true)

	const {
		control,
		setValue,
		setError,
		clearErrors,
		trigger,
		formState: { isValid, errors }
	} = useForm({
		mode: 'onChange',
		reValidateMode: 'onChange',
		defaultValues: formValues
	})
	const tagCreationFormStore = useGenericTagsManagementFormStore()
	const { mutate: createTags } = useCreateNewTags()

	useEffect(() => {
		const portfolioValue = formValues.portfolio?.value ?? 'demo'
		const projectNameValue = portfolioValue + '-' + formValues.application
		setValue('projectName', projectNameValue)
		setFormValues('projectName', projectNameValue)
	}, [formValues.portfolio?.value, formValues.application, setValue, setFormValues])

	useEffect(() => {
		setFormValidity(isValid)
	}, [isValid, setFormValidity])

	const distributionListOptions = useMemo(
		() =>
			buildProjectDistributionListOptions({
				accounts: (accounts ?? []) as Account[],
				clusters: (clustersList ?? []) as Cluster[],
				environment: formValues.environment
			}),
		[accounts, clustersList, formValues.environment]
	)

	const selectableDistributionListCount = useMemo(
		() => distributionListOptions.filter((o) => !o.disabled).length,
		[distributionListOptions]
	)

	const selectedDistributionList = resolveDistributionListValue(
		formValues.distributionList
	)

	const accountTagsRequired = tagsViewToggler && !!selectedDistributionList
	const newTagsRequired = !tagsViewToggler && !!selectedDistributionList
	const accountTagsReady = tagsLoadStatus === 'loaded' && !!getTagsId
	const submitBlockedByTags =
		(accountTagsRequired && (tagsLoadStatus === 'loading' || !accountTagsReady)) ||
		(newTagsRequired && !tagCreationFormStore.isFormValid)

	const clearDistributionListSelection = () => {
		setValue('distributionList', null)
		setFormValues('distributionList', '')
		setValue('awsAccountID', '')
		setFormValues('awsAccountID', '')
		setValue('accountWorkloadType', '')
		setFormValues('accountWorkloadType', '')
		setTagsId('')
		setTagsLoadStatus('idle')
	}

	useEffect(() => {
		const dl = resolveDistributionListValue(formValues.distributionList)
		if (dl) {
			getTagsByDLId()
		}
		// eslint-disable-next-line
	}, [formValues.distributionList])

	useEffect(() => {
		const dl = resolveDistributionListValue(formValues.distributionList)
		if (!dl) return
		const option = distributionListOptions.find(
			(o) => o.value.toLowerCase() === dl.toLowerCase()
		)
		if (!option || option.disabled) {
			clearDistributionListSelection()
		}
		// eslint-disable-next-line
	}, [distributionListOptions, formValues.environment])

	const handleSubmit = async () => {
		await trigger()
		const ssoListUsersList = formValues.ssoListUsers || []
		const ssoListUsersValid =
			ssoListUsersList.length > 0 &&
			ssoListUsersList.every(
				(u: { displayName?: string; networkId?: string }) =>
					(u?.displayName ?? '').trim() !== '' && (u?.networkId ?? '').trim() !== ''
			)
		if (!ssoListUsersValid) {
			setError('ssoListUsers', {
				type: 'manual',
				message:
					'At least one SSO list user is required. Each user must have both Display Name and Network ID.'
			})
			return
		}
		clearErrors('ssoListUsers')
		if (!isValid) return

		if (tagsViewToggler) {
			if (tagsLoadStatus !== 'loaded' || !getTagsId) {
				setError('distributionList', {
					type: 'manual',
					message:
						'Account tags must load successfully before submitting. Select a different distribution list or retry.'
				})
				return
			}
		} else if (selectedDistributionList && !tagCreationFormStore.isFormValid) {
			return
		}
		clearErrors('distributionList')

		const tagsID = uuidv4()
		const ssoListUsersPayload = ssoListUsersList as Array<{
			displayName?: string
			networkId?: string
		}>
		const ssoListApproversPayload = (formValues.ssoListApprovers || []) as Array<{
			displayName?: string
			networkId?: string
		}>
		const detailsForProjectApi = {
			...formValues,
			distributionList: resolveDistributionListValue(formValues.distributionList),
			ssoListUsers: ssoListUsersPayload,
			ssoListApprovers: ssoListApproversPayload,
			accountWorkloadType: formValues.accountWorkloadType || 'Non-EKS',
			SSOUserNames: ssoListUsersPayload.map((u) => ({
				displayName: u?.displayName ?? '',
				userName: u?.networkId ?? ''
			}))
		}
		const fundingChoice = tagsViewToggler ? 'inherit' : 'own'
		const tagDetails = tagsViewToggler
			? fundingDetailsFromParent(accountTags?.details || {})
			: tagCreationFormStore.formValues
		const project: ProjectRequest = {
			requestorID: thisUserEmail,
			action: 'CREATE',
			details: {
				...detailsForProjectApi,
				fundingChoice,
				parentTagsID: getTagsId
			},
			status: 'PENDING_APPROVAL',
			tagsID
		}
		const tagRequest: TagsRequest = buildAwsFundingTagRequest({
			requestorID: thisUserEmail,
			tagsID,
			parentTagsID: getTagsId,
			fundingChoice,
			details: tagDetails
		})
		createTags(tagRequest, {
			onSuccess: () => {
				createNewProject(project, {
					onSuccess: () => navigate('/projects')
				})
			}
		})
	}
	const getTagsByDLId = () => {
		const dl = resolveDistributionListValue(formValues.distributionList)
		if (!dl) return
		setTagsLoadStatus('loading')
		setTagsId('')
		getTagsByAccountId(dl, {
			onSuccess: (data: { status: number; body: object[] }) => {
				const response = data?.body?.[0] as Record<string, unknown> | undefined
				if (!response?.tagsID) {
					setTagsLoadStatus('error')
					setTagsId('')
					return
				}
				setTagsLoadStatus('loaded')
				setTagsId(String(response.tagsID))
				const accountIDFromResponse =
					response.accountID != null
						? String(response.accountID)
						: (response.details as Record<string, unknown> | undefined)?.awsAccountID != null
							? String((response.details as Record<string, unknown>).awsAccountID)
							: ''
				if (accountIDFromResponse) {
					setValue('awsAccountID', accountIDFromResponse)
					setFormValues('awsAccountID', accountIDFromResponse)
				}
				const details = response.details as Record<string, unknown> | undefined
				const ssoUserList = (details?.SSOUserNames ?? details?.sso_user_list) as Array<{
					displayName?: string
					userName?: string
				}> | undefined
				if (Array.isArray(ssoUserList) && ssoUserList.length > 0) {
					const ssoListUsersFromAccount = ssoUserList.map((u) => ({
						displayName: String(u?.displayName ?? ''),
						networkId: String(u?.userName ?? '')
					}))
					setValue('ssoListUsers', ssoListUsersFromAccount)
					setFormValues('ssoListUsers', ssoListUsersFromAccount)
				}
				const rawWorkloadType = details?.accountWorkloadType
				const accountWorkloadTypeFromResponse = normalizeAccountWorkloadType(rawWorkloadType)
				setValue('accountWorkloadType', accountWorkloadTypeFromResponse)
				setFormValues('accountWorkloadType', accountWorkloadTypeFromResponse)
			},
			onError: () => {
				setTagsLoadStatus('error')
				setTagsId('')
			}
		})
	}
	return (
		<Form
			actions={
				<SpaceBetween direction="horizontal" size="xs">
					<Box>
						<Button
							variant="primary"
							onClick={handleSubmit}
							loading={isLoading || isLoadingTags}
							disabled={submitBlockedByTags}
						>
							Submit
						</Button>
						<Button variant="link" onClick={() => navigate(-1)}>
							Cancel
						</Button>
					</Box>
				</SpaceBetween>
			}
			header={
				<Header variant="h1" description="">
					Request a New Project
				</Header>
			}
		>
			<SpaceBetween direction="vertical" size="s">
				<Container>
					<SpaceBetween direction="vertical" size="l">
						<FormInput
							name="awsAccountID"
							label="AWS Account ID"
							placeholder='Enter AWS Account ID'
							type="number"
							control={control}
							rules={{
								required: 'Please enter your AWS Account ID',
								minLength: {
									value: 12,
									message: 'Minimum length should be 12'
								},
								maxLength: {
									value: 12,
									message: 'Maximum length should be 12'
								}
							}}
							setFormValues={setFormValues}
						/>

						{!isLoadingAccounts &&
							!isLoadingClusters &&
							distributionListOptions.length === 0 && (
								<Alert type="warning" header="No approved accounts">
									No approved accounts are available. Onboard and approve an account
									first.
								</Alert>
							)}

						{!isLoadingAccounts &&
							!isLoadingClusters &&
							distributionListOptions.length > 0 &&
							selectableDistributionListCount === 0 && (
								<Alert type="warning" header="No accounts ready for project onboarding">
									Approved accounts exist, but EKS accounts require a matching
									approved cluster for the selected environment. Complete cluster
									onboarding first, or select a Non-EKS account.
								</Alert>
							)}

						<FormSelect
							name="distributionList"
							label="Distribution List"
							control={control}
							options={distributionListOptions}
							placeholder="Select an approved account distribution list"
							statusType={
								isLoadingAccounts || isLoadingClusters ? 'loading' : undefined
							}
							rules={{
								required: 'Please select a distribution list',
								validate: (value) => {
									const dl = resolveDistributionListValue(value)
									if (!dl) return 'Please select a distribution list'
									const option = distributionListOptions.find(
										(o) => o.value.toLowerCase() === dl.toLowerCase()
									)
									if (!option) return 'Selected distribution list is not available'
									if (option.disabled) {
										return 'Cluster onboarding must be completed for this account before project onboarding'
									}
									return true
								}
							}}
							setFormValues={setFormValues}
						/>

						<FormField label="Workload Type">
							<RadioGroup
								value={formValues.accountWorkloadType || 'Non-EKS'}
								items={[
									{ value: 'EKS', label: 'EKS', disabled: true },
									{ value: 'Non-EKS', label: 'Non-EKS', disabled: true }
								]}
							/>
						</FormField>


						<FormMultiselect
							name="region"
							label="Region"
							control={control}
							placeholder="Select a region"
							options={[
								{
									label: 'Singapore',
									value: 'singapore',
									description: 'ap-southeast-1'
								}
							]}
							rules={{
								required: 'Please select at least one region',
								validate: (value) =>
									value.length > 0 || 'Select at least one region'
							}}
							setFormValues={setFormValues}
						/>

						{/* <FormRequiredTags
							name="projectDetails"
							label="Project Details"
							control={control}
							tagKeys={projectKeys}
							setFormValues={setFormValues}
						/> */}

						<FormField label="Project Details" description="">
						<ColumnLayout columns={3} variant="default">
							<FormSelect
							name="deployment"
							label="Deployment"
							control={control}
							rules={{ required: 'Please select Deployment' }}
							setFormValues={setFormValues}
							options={[
								{ label: 'DEV:PREP', value: 'DEV:PREP' },
								{ label: 'SB:PREP', value: 'SB:PREP' },
								{ label: 'STAGING:LIVE', value: 'STAGING:LIVE' },
								{ label: 'STAGING:PREP', value: 'STAGING:PREP' },
								{ label: 'PROD:LIVE', value: 'PROD:LIVE' },
								{ label: 'PROD:PREP', value: 'PROD:PREP' }
							]}
							placeholder="Select Deployment"
							/>

							<FormSelect
							name="dataClassification"
							label="Data Classification"
							control={control}
							rules={{ required: 'Please select Data Classification' }}
							setFormValues={setFormValues}
							options={[
								{ label: 'PUBLIC', value: 'PUBLIC' },
								{ label: 'PRIVATE', value: 'PRIVATE' },
								{ label: 'CONFIDENTIAL-PROPRIETARY', value: 'CONFIDENTIAL-PROPRIETARY' },
								{ label: 'CONFIDENTIAL-PERSONAL', value: 'CONFIDENTIAL-PERSONAL' }
							]}
							placeholder="Select Data Classification"
							/>

							<FormSelect
							name="tierLevel"
							label="Tier Level"
							control={control}
							rules={{ required: 'Please select Tier Level' }}
							setFormValues={setFormValues}
							options={[
								{ label: '1', value: '1' },
								{ label: '2a', value: '2a' },
								{ label: '2b', value: '2b' },
								{ label: '3', value: '3' },
								{ label: 'nonprod', value: 'nonprod' },
							]}
							placeholder="Select Tier Level"
							/>
						</ColumnLayout>
						</FormField>


						<FormField label="" description="">
							<ColumnLayout columns={3} variant="default">
								<FormInput
								    name="application"
                                    label="Application"
                                    type="text"
                                    placeholder="Enter Application"
                                    control={control}
                                    rules={{
                                        required: 'Application should be 5 letters',
                                        pattern: {
                                            value: /^[A-Za-z][A-Za-z0-9-]{4,}$/g,
                                            message: 'Application should be 5 letters'
                                        },
                                        minLength: {
                                            value: 5,
                                            message: 'Application should be 5 letters'
                                        },
                                        maxLength: {
                                            value: 5,
                                            message: 'System alias should be 5 letters, Example: namax'
                                        }
                                    }}
                                    setFormValues={setFormValues}
                            />
								<FormSelect
									label="Portfolio"
									name="portfolio"
									control={control}
									rules={{
										required: 'Please Select Option'
									}}
									setFormValues={setFormValues}
									options={PORTFOLIO_LIST}
									placeholder="Select Portfolio"	
								/>
								<FormSelect
									label="Environment"
									name="environment"
									control={control}
									rules={{
										required: 'Please Select Option'
									}}
									setFormValues={setFormValues}
									options={ENVIRONMENT_LIST}
									placeholder="Select Environment"
								/>
								<FormInput
									name="budget"
									label="Project Budget Allocated"
									description="In US Dollar (USD) currency"
									type="text"
									rules={{
										required: 'Please enter Project Budget allocated',
										pattern: {
											value: /(?=.*?\d)^\$?(([1-9]\d{0,2}(,\d{3})*)|\d+)?(\.\d{1,2})?$/,
											message:
												'Please enter valid Project Budget allocated'
										},
									}}
									
									control={control}
									setFormValues={setFormValues}
									placeholder="0"
								/>
								<FormDatePicker
									name="crfsDate"
									label="CRFS Date"
									description="Customer Ready for Service Date"
									rules={{
										required: 'Please Enter CRFS Date'

									}}
									control={control}
									setFormValues={setFormValues}
								/>
								{/* <FormInput
									name="customProjectID"
									label="Project ID"
									description="12 alphanumeric characters only"
									type="text"
									rules={{
										required: 'Please enter valid project ID',
										pattern: {
											value: /^[a-zA-Z0-9_]*$/,
											message: 'Please enter valid project ID'
										},
										minLength: {
                                            value: 12,
                                            message: 'ProjectID should be 12 letters'
                                        },
                                        maxLength: {
                                            value: 12,
                                            message: 'ProjectID should be 12 letters'
                                        }
									}}
									control={control}
									setFormValues={setFormValues}
									placeholder=""
								/> */}
								
								<FormInput
									name="projectName"
									label="Project Name"
									type="text"
									control={control}
									setFormValues={setFormValues}
									placeholder=""
									disabled={true}
								/>
								
							</ColumnLayout>
						</FormField>

						<FormTagEditor
							label="Additional Tags"
							name="tags"
							control={control}
							setFormValues={setFormValues}
							tags={[]}
						/>

						<FormField
							label="SSO List Users"
							description="Users List for TEAMs Onboarding"
							errorText={errors.ssoListUsers?.message as string}
						>
							<SpaceBetween direction="vertical" size="s">
								{(formValues.ssoListUsers || []).map(
									(
										user: { displayName?: string; networkId?: string },
										index: number
									) => (
										<ColumnLayout key={index} columns={3} variant="text-grid">
											<FormField label="Display Name">
												<Input
													value={user?.displayName ?? ''}
													onChange={({ detail }) => {
														clearErrors('ssoListUsers')
														const next = [
															...(formValues.ssoListUsers || [])
														].map((u, i) =>
															i === index
																? { ...u, displayName: detail.value }
																: u
														)
														setValue('ssoListUsers', next)
														setFormValues('ssoListUsers', next)
													}}
													placeholder="Display name"
												/>
											</FormField>
											<FormField label="Network ID">
												<Input
													value={user?.networkId ?? ''}
													onChange={({ detail }) => {
														clearErrors('ssoListUsers')
														const next = [
															...(formValues.ssoListUsers || [])
														].map((u, i) =>
															i === index
																? { ...u, networkId: detail.value }
																: u
														)
														setValue('ssoListUsers', next)
														setFormValues('ssoListUsers', next)
													}}
													placeholder="e.g. user@globetel.com"
												/>
											</FormField>
											<Box padding={{ top: 'l' }}>
												<Button
													variant="normal"
													iconName="remove"
													onClick={() => {
														clearErrors('ssoListUsers')
														const next = (formValues.ssoListUsers || []).filter(
															(_: unknown, i: number) => i !== index
														)
														setValue('ssoListUsers', next)
														setFormValues('ssoListUsers', next)
													}}
												>
													Remove
												</Button>
											</Box>
										</ColumnLayout>
									)
								)}
								<Button
									variant="normal"
									iconName="add-plus"
									onClick={() => {
										clearErrors('ssoListUsers')
										const next = [
											...(formValues.ssoListUsers || []),
											{ displayName: '', networkId: '' }
										]
										setValue('ssoListUsers', next)
										setFormValues('ssoListUsers', next)
									}}
								>
									Add User
								</Button>
							</SpaceBetween>
						</FormField>

						<FormField
							label="SSO list approvers"
							description="Approver List for TEAM Onboarding"
						>
							<SpaceBetween direction="vertical" size="s">
								{(formValues.ssoListApprovers || []).map(
									(
										approver: { displayName?: string; networkId?: string },
										index: number
									) => (
										<ColumnLayout key={index} columns={3} variant="text-grid">
											<FormField label="Display Name">
												<Input
													value={approver?.displayName ?? ''}
													onChange={({ detail }) => {
														const next = [
															...(formValues.ssoListApprovers || [])
														].map((u, i) =>
															i === index
																? { ...u, displayName: detail.value }
																: u
														)
														setValue('ssoListApprovers', next)
														setFormValues('ssoListApprovers', next)
													}}
													placeholder="Text Input"
												/>
											</FormField>
											<FormField label="Network ID">
												<Input
													value={approver?.networkId ?? ''}
													onChange={({ detail }) => {
														const next = [
															...(formValues.ssoListApprovers || [])
														].map((u, i) =>
															i === index
																? { ...u, networkId: detail.value }
																: u
														)
														setValue('ssoListApprovers', next)
														setFormValues('ssoListApprovers', next)
													}}
													placeholder="e.g. 11111111@globetel.com"
												/>
											</FormField>
											<Box padding={{ top: 'l' }}>
												<Button
													variant="normal"
													iconName="remove"
													onClick={() => {
														const next = (formValues.ssoListApprovers || []).filter(
															(_: unknown, i: number) => i !== index
														)
														setValue('ssoListApprovers', next)
														setFormValues('ssoListApprovers', next)
													}}
												>
													Remove
												</Button>
											</Box>
										</ColumnLayout>
									)
								)}
								<Button
									variant="normal"
									iconName="add-plus"
									onClick={() => {
										const next = [
											...(formValues.ssoListApprovers || []),
											{ displayName: '', networkId: '' }
										]
										setValue('ssoListApprovers', next)
										setFormValues('ssoListApprovers', next)
									}}
								>
									Add Approver
								</Button>
							</SpaceBetween>
						</FormField>

						<ExpandableSection headerText="Optional Input(s)">
							<SpaceBetween direction="vertical" size="l">
								<FormTokenGroup
									label="Workload AMI Names"
									name="workloadAMINames"
									control={control}
									items={[]}
									placeholder="Type WorkloadAMINames and press 'Enter'"
									setFormValues={setFormValues}
								/>

								<FormTokenGroup
									label="Project Users"
									name="projectUsers"
									control={control}
									items={[]}
									placeholder="Type Users and press 'Enter'"
									setFormValues={setFormValues}
								/>

								<FormTokenGroup
									label="SecurityAliases Whitelist"
									name="securityAliasesWhitelist"
									control={control}
									items={[]}
									placeholder="Type SecurityAlias and press 'Enter'"
									setFormValues={setFormValues}
								/>

								<FormTokenGroup
									label="PRN Whitelist"
									name="prnWhitelist"
									control={control}
									items={[]}
									placeholder="Type PRN and press 'Enter'"
									setFormValues={setFormValues}
								/>

								<FormDayTime
									name="maintenanceWindow"
									label="Maintenance Window"
									description="Optional | Defaults to Tuesday 1:00 AM GMT+8"
									control={control}
									options={weekDaySelectOptions}
									placeholder="Select a day and time"
									setFormValues={setFormValues}
								/>
							</SpaceBetween>
						</ExpandableSection>
					</SpaceBetween>
				</Container>
				{selectedDistributionList && (
					<Container
						header={
							<Header
								variant="h2"
								description=""
								actions={
									getTagsId ||
									(!tagsViewToggler && selectedDistributionList) ? (
										<SpaceBetween direction="horizontal" size="xs">
											{tagsViewToggler ? (
												<Button
													variant="link"
													onClick={() => setTagsViewToggler(!tagsViewToggler)}
												>
													+ Create New Tags
												</Button>
											) : (
												<Button
													variant="link"
													onClick={() => setTagsViewToggler(!tagsViewToggler)}
												>
													- Switch to Original Tags
												</Button>
											)}
										</SpaceBetween>
									) : tagsViewToggler && tagsLoadStatus === 'error' ? (
										<Button
											variant="link"
											onClick={() => setTagsViewToggler(false)}
										>
											+ Create New Tags
										</Button>
									) : undefined
								}
							>
								Tags Details
							</Header>
						}
					>
						<SpaceBetween direction="vertical" size="m">
							{tagsViewToggler && tagsLoadStatus === 'error' && (
								<Alert type="error" header="Account tags could not be loaded">
									Tags for the selected account failed to load. Submit is disabled
									until tags load successfully. Try selecting the distribution list
									again, or use Create New Tags once tags are available.
								</Alert>
							)}
							{tagsViewToggler && tagsLoadStatus === 'loading' && (
								<Alert type="info" header="Loading account tags">
									Loading tags for the selected account. Submit will be enabled once
									tags load successfully.
								</Alert>
							)}
							{!tagsViewToggler && !tagCreationFormStore.isFormValid && (
								<Alert type="info" header="Complete tag details">
									Fill in all required tag fields before submitting.
								</Alert>
							)}
							{getTagsId || !tagsViewToggler ? (
								<FormField label="">
									<ColumnLayout columns={1} variant="default">
										{tagsViewToggler ? (
											<>
												<ReadGenericTags
													tagsId={getTagsId}
													layout={3}
													showApprovalStatus={false}
												/>
												<ApproverOverview
													approvalStatus={getAwsFundingApprovals()}
													isDisabled
												/>
											</>
										) : (
											<>
												<GenericTagsNew />
												<ApproverOverview
													approvalStatus={getAwsFundingApprovals()}
													isDisabled
												/>
											</>
										)}
									</ColumnLayout>
								</FormField>
							) : null}
						</SpaceBetween>
					</Container>
				)}
			</SpaceBetween>
		</Form>
	)
}

export default NewProjectForm
